import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "./SpecialCharacterModal.css";

interface SpecialCharacterModalProps {
  show: boolean;
  onHide: () => void;
  onInsert: (char: string) => void;
}

interface CharCategory {
  key: string;
  label: string;
  chars: string[];
}

// Categories weighted toward legal / patent drafting needs.
const CATEGORIES: CharCategory[] = [
  {
    key: "legal",
    label: "Legal & marks",
    chars: [
      "§",
      "¶",
      "©",
      "®",
      "™",
      "℠",
      "†",
      "‡",
      "№",
      "℗",
      "°",
      "′",
      "″",
      "‰",
      "※",
    ],
  },
  {
    key: "punctuation",
    label: "Punctuation",
    chars: [
      "—",
      "–",
      "…",
      "•",
      "·",
      "‚",
      "„",
      "‘",
      "’",
      "“",
      "”",
      "«",
      "»",
      "¡",
      "¿",
      "⁓",
    ],
  },
  {
    key: "math",
    label: "Math & symbols",
    chars: [
      "±",
      "×",
      "÷",
      "≤",
      "≥",
      "≠",
      "≈",
      "≡",
      "∞",
      "√",
      "∑",
      "∏",
      "∫",
      "∂",
      "∆",
      "µ",
      "∅",
      "∈",
      "∉",
      "⊂",
      "⊆",
      "∪",
      "∩",
      "→",
    ],
  },
  {
    key: "currency",
    label: "Currency & fractions",
    chars: [
      "$",
      "¢",
      "£",
      "€",
      "¥",
      "₩",
      "₽",
      "₹",
      "¤",
      "½",
      "⅓",
      "¼",
      "¾",
      "⅛",
    ],
  },
  {
    key: "arrows",
    label: "Arrows",
    chars: ["←", "→", "↑", "↓", "↔", "↕", "⇐", "⇒", "⇑", "⇓", "⇔", "↦", "⟶", "⟵"],
  },
  {
    key: "greek",
    label: "Greek",
    chars: [
      "α",
      "β",
      "γ",
      "δ",
      "ε",
      "θ",
      "λ",
      "μ",
      "π",
      "ρ",
      "σ",
      "τ",
      "φ",
      "χ",
      "ψ",
      "ω",
      "Δ",
      "Θ",
      "Λ",
      "Π",
      "Σ",
      "Φ",
      "Ω",
    ],
  },
];

const SpecialCharacterModal: React.FC<SpecialCharacterModalProps> = ({
  show,
  onHide,
  onInsert,
}) => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].key);
  const [recent, setRecent] = useState<string[]>([]);

  const insert = (char: string) => {
    onInsert(char);
    setRecent(prev => [char, ...prev.filter(c => c !== char)].slice(0, 12));
    // Keep the picker open for inserting several characters in a row.
  };

  const current =
    CATEGORIES.find(c => c.key === activeCategory) || CATEGORIES[0];

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {t("tiptapEditor.specialChar.title", {
            defaultValue: "Insert special character",
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {recent.length > 0 && (
          <div className="spc-recent">
            <div className="spc-section-label">
              {t("tiptapEditor.specialChar.recent", {
                defaultValue: "Recently used",
              })}
            </div>
            <div className="spc-grid">
              {recent.map((char, i) => (
                <button
                  key={`recent-${i}`}
                  type="button"
                  className="spc-cell"
                  onClick={() => insert(char)}
                  title={char}
                >
                  {char}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="spc-tabs">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              type="button"
              className={`spc-tab ${
                cat.key === activeCategory ? "active" : ""
              }`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {t(`tiptapEditor.specialChar.category.${cat.key}`, {
                defaultValue: cat.label,
              })}
            </button>
          ))}
        </div>

        <div className="spc-grid">
          {current.chars.map((char, i) => (
            <button
              key={`${current.key}-${i}`}
              type="button"
              className="spc-cell"
              onClick={() => insert(char)}
              title={char}
            >
              {char}
            </button>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SpecialCharacterModal;
